import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  AccessTime as ClockIcon,
  Login as ClockInIcon,
  Logout as ClockOutIcon,
  Schedule as ScheduleIcon,
  Today as TodayIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { makeApiCall } from '../../config/api';

const TimeTrackingWidget = ({ compact = false }) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [timeEntries, setTimeEntries] = useState([]);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null });
  const [notes, setNotes] = useState('');
  const [isEmployee, setIsEmployee] = useState(false);
  const [employee, setEmployee] = useState(null);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load time entries and current status
  useEffect(() => {
    loadTimeTracking();
  }, []);

  const loadTimeTracking = async () => {
    try {
      setLoading(true);
      const response = await makeApiCall('api/time-tracking/current-status');
      if (response.ok) {
        const data = await response.json();
        setIsEmployee(data.isEmployee);
        setEmployee(data.employee);
        setCurrentStatus(data.currentStatus);
        setTimeEntries(data.todayEntries || []);
        
        // If user is not an employee, show the appropriate message
        if (!data.isEmployee) {
          setError(data.error || 'Time tracking not available');
        }
      } else {
        setError(t('common.loading'));
      }
    } catch (error) {
      console.error('Error loading time tracking:', error);
      setError(t('common.loading'));
    } finally {
      setLoading(false);
    }
  };

  const handleClockAction = async (action) => {
    try {
      setActionLoading(true);
      setError('');
      
      const response = await makeApiCall(`api/time-tracking/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: notes.trim() || undefined })
      });

      if (response.ok) {
        await loadTimeTracking();
        setConfirmDialog({ open: false, type: null });
        setNotes('');
      } else {
        const errorData = await response.json();
        if (errorData.code === 'NOT_EMPLOYEE') {
          // Handle NOT_EMPLOYEE error specifically
          setError(errorData.error);
          setIsEmployee(false);
        } else {
          setError(errorData.error || `Failed to ${action}`);
        }
      }
    } catch (error) {
      console.error(`Error ${action}:`, error);
      setError(`Failed to ${action}`);
    } finally {
      setActionLoading(false);
    }
  };

  const formatTime = (date) => {
    const locale = i18n.language || 'en-US';
    return new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  const formatDate = (date) => {
    const locale = i18n.language || 'en-US';
    return new Intl.DateTimeFormat(locale, {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const calculateWorkingTime = () => {
    if (!currentStatus || currentStatus.status !== 'clocked_in') {
      return null;
    }

    const clockInTime = new Date(currentStatus.clock_in_time);
    const now = new Date();
    const diffMs = now - clockInTime;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };

  const getTotalHoursToday = () => {
    let totalMs = 0;
    
    timeEntries.forEach(entry => {
      if (entry.clock_out_time) {
        const clockIn = new Date(entry.clock_in_time);
        const clockOut = new Date(entry.clock_out_time);
        totalMs += clockOut - clockIn;
      }
    });

    // Add current session if clocked in
    if (currentStatus && currentStatus.status === 'clocked_in') {
      const clockIn = new Date(currentStatus.clock_in_time);
      const now = new Date();
      totalMs += now - clockIn;
    }

    const hours = Math.floor(totalMs / (1000 * 60 * 60));
    const minutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };

  const openConfirmDialog = (type) => {
    if (!isEmployee) return; // Don't open dialog for non-employees
    setConfirmDialog({ open: true, type });
    setNotes('');
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, type: null });
    setNotes('');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: compact ? 96 : 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: compact ? 'none' : 800, mx: 'auto' }}>
      {/* Main Clock Display */}
      {!compact && (
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #000062 0%, #de6127 100%)', color: 'white' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" sx={{ mb: 1, opacity: 0.9 }}>
              <ClockIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('timeTracking.currentTime')}
            </Typography>
            <Typography variant="h2" sx={{ mb: 1, fontWeight: 'bold' }}>
              {formatTime(currentTime)}
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              {formatDate(currentTime)}
            </Typography>
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: compact ? 1.5 : 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={compact ? 0 : 3}>
        {/* Clock In/Out Controls */}
        <Grid size={{ xs: 12, md: compact ? 12 : 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: compact ? 2 : undefined }}>
              <Typography variant="h6" gutterBottom>
                {t('timeTracking.timeTracking')}
                {employee && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('timeTracking.employeeLabel')}: {employee.employee_name}
                  </Typography>
                )}
              </Typography>
              
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                {!isEmployee ? (
                  <>
                    <Chip 
                      label={t('timeTracking.notAvailable')}
                      color="error"
                      sx={{ mb: 2, px: 2, py: 1 }}
                    />
                    <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
                      {t('timeTracking.employeeLinkRequired')}
                    </Alert>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={<ClockInIcon />}
                      disabled
                      fullWidth
                      aria-label={t('timeTracking.clockInUnavailable')}
                      sx={{ mt: 2 }}
                    >
                      {t('timeTracking.clockInUnavailable')}
                    </Button>
                  </>
                ) : currentStatus && currentStatus.status === 'clocked_in' ? (
                  <>
                    <Chip 
                      label={t('timeTracking.clockedIn')}
                      color="success"
                      sx={{ mb: 2, px: 2, py: 1, fontWeight: 'bold' }}
                    />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('timeTracking.since')} {formatTime(new Date(currentStatus.clock_in_time))}
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
                      {t('timeTracking.working')} {calculateWorkingTime()}
                    </Typography>
                    <Button
                      variant="contained"
                      color="error"
                      size="large"
                      startIcon={<ClockOutIcon />}
                      onClick={() => openConfirmDialog('clock-out')}
                      disabled={actionLoading}
                      fullWidth
                      aria-label={t('timeTracking.clockOut')}
                    >
                      {t('timeTracking.clockOut')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Chip 
                      label={t('timeTracking.clockedOut')}
                      color="default"
                      sx={{ mb: 2, px: 2, py: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('timeTracking.readyToStartDay')}
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      size="large"
                      startIcon={<ClockInIcon />}
                      onClick={() => openConfirmDialog('clock-in')}
                      disabled={actionLoading}
                      fullWidth
                      aria-label={t('timeTracking.clockIn')}
                      sx={{ mt: 3 }}
                    >
                      {t('timeTracking.clockIn')}
                    </Button>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Today's Summary */}
        {!compact && (
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%', opacity: !isEmployee ? 0.6 : 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TodayIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('timeTracking.todaysSummary')}
              </Typography>

              {!isEmployee ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t('timeTracking.summaryRequiresEmployee')}
                </Alert>
              ) : (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('timeTracking.totalHours')}
                    </Typography>
                    <Typography variant="h5" color="primary">
                      {getTotalHoursToday()}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('timeTracking.sessionsToday')}: {timeEntries.length}
                    </Typography>
                  </Box>

                  {/* Recent entries */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('timeTracking.recentActivity')}
                    </Typography>
                    {timeEntries.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        {t('timeTracking.noActivityToday')}
                      </Typography>
                    ) : (
                      timeEntries.slice(0, 3).map((entry, index) => (
                        <Paper 
                          key={entry.id} 
                          variant="outlined" 
                          sx={{ p: 1, mb: 1, bgcolor: 'grey.50' }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption">
                              {formatTime(new Date(entry.clock_in_time))}
                              {entry.clock_out_time && ` - ${formatTime(new Date(entry.clock_out_time))}`}
                            </Typography>
                            <Chip 
                              label={entry.clock_out_time ? t('timeTracking.complete') : t('timeTracking.active')}
                              size="small"
                              color={entry.clock_out_time ? "default" : "success"}
                            />
                          </Box>
                        </Paper>
                      ))
                    )}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        )}
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog 
        open={confirmDialog.open} 
        onClose={closeConfirmDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmDialog.type === 'clock-in' ? (
            <>
              <ClockInIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('timeTracking.clockIn')}
            </>
          ) : (
            <>
              <ClockOutIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('timeTracking.clockOut')}
            </>
          )}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {confirmDialog.type === 'clock-in' 
              ? t('timeTracking.confirmClockIn') 
              : t('timeTracking.confirmClockOut')
            }
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            {t('timeTracking.timeLabel')} {formatTime(currentTime)}
          </Typography>
          
          <TextField
            label={t('timeTracking.notesOptional')}
            multiline
            rows={3}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('timeTracking.notesOptional')}
            inputProps={{ 'aria-label': t('timeTracking.notesOptional') }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} aria-label={t('common.cancel')}>{t('common.cancel')}</Button>
          <Button 
            onClick={() => handleClockAction(confirmDialog.type)}
            variant="contained"
            disabled={actionLoading}
            color={confirmDialog.type === 'clock-in' ? 'success' : 'error'}
            aria-label={confirmDialog.type === 'clock-in' ? t('timeTracking.clockIn') : t('timeTracking.clockOut')}
          >
            {actionLoading ? <CircularProgress size={20} /> : 
              (confirmDialog.type === 'clock-in' ? t('timeTracking.clockIn') : t('timeTracking.clockOut'))
            }
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimeTrackingWidget;
